import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useSeo } from "@/hooks/useSeo";
import { PageHeader } from "@/components/site/PageHeader";
import { Reveal, Eyebrow } from "@/components/site/Reveal";
import { Marquee } from "@/components/site/Marquee";
import { IMAGES, CREDENTIALS } from "@/data/content";

const team = [
  {
    name: "Thiago",
    role: "Owner & Project Manager",
    img: "https://customer-assets-0z36b82j.emergentagent.net/job_coastal-home-hvac/artifacts/n0kynnak_Thiago%20Headshot.png",
    body: "Oversees projects, quality control, and the business as a whole — making sure every installation meets the Lighthouse standard.",
  },
  {
    name: "Nayara",
    role: "Operations Manager",
    img: "https://customer-assets-0z36b82j.emergentagent.net/job_coastal-home-hvac/artifacts/vcwyl2c8_Nayara%20Headshot.png",
    body: "Manages scheduling, communication, permits, and coordination — keeping every project running smoothly from beginning to end.",
  },
];

export default function About() {
  useSeo({
    title: "About Us | Family-Owned HVAC Company in Massachusetts",
    description:
      "Lighthouse Home Services is a family-owned HVAC company in Oxford, MA — 800+ high-efficiency heat pump & mini split installations, Mass Save HPIN & Mitsubishi Diamond Contractor.",
  });

  return (
    <div>
      <PageHeader
        eyebrow="About Us"
        title={<>Family-owned. <br /> Built the right way.</>}
        intro="We started Lighthouse Home Services with a simple belief: homeowners deserve an HVAC company that treats them — and their homes — like family."
        image={IMAGES.aboutHero}
      />

      {/* OUR STORY */}
      <section className="mx-auto max-w-7xl px-5 md:px-8 py-24 md:py-32">
        <div className="grid gap-14 lg:grid-cols-12 items-center">
          <div className="lg:col-span-6 order-2 lg:order-1 overflow-hidden rounded-3xl">
            <img
              src={IMAGES.crew}
              alt="The Lighthouse Home Services installation team"
              className="w-full h-[26rem] object-cover object-top"
            />
          </div>
          <div className="lg:col-span-6 order-1 lg:order-2">
            <Reveal><Eyebrow>Our Story</Eyebrow></Reveal>
            <Reveal delay={0.05}>
              <h2 className="mt-6 font-serif text-4xl md:text-5xl leading-tight text-navy">
                Rooted in Oxford, dedicated to Massachusetts.
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="mt-6 space-y-5 text-muted-foreground leading-relaxed">
                <p>
                  Lighthouse Home Services is a family-owned HVAC company based in Oxford, Massachusetts. Since 2021, we've specialized in high-quality heat pump and mini split installations for homeowners across the Commonwealth — more than 800 completed projects and counting.
                </p>
                <p>
                  We're proud to be a Mass Save® Heat Pump Installer Network (HPIN) Contractor and a Mitsubishi Diamond Contractor. Every installation is completed with exceptional craftsmanship, honest communication, and real respect for your property — our mission is to make replacing your heating and cooling system simple, organized, and stress-free.
                </p>
                <p>
                  We believe upgrading your home's comfort should do more than keep you cozy. It should improve efficiency, reduce energy use, and create a healthier, better home for years to come.
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="mt-7 flex flex-wrap gap-3">
                {CREDENTIALS.map((c) => (
                  <span key={c} className="inline-flex items-center rounded-full bg-navy px-4 py-2 text-xs uppercase tracking-[0.18em] text-gold">{c}</span>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* MEET THE TEAM */}
      <section className="bg-sand py-24 md:py-32" data-testid="team">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <div className="max-w-xl">
            <Reveal><Eyebrow>Our Team</Eyebrow></Reveal>
            <Reveal delay={0.05}>
              <h2 className="mt-6 font-serif text-4xl md:text-5xl leading-tight text-navy">
                Meet the team you'll trust in your home.
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-5 text-muted-foreground">A family-owned team that treats your home like our own — and genuinely cares about doing things the right way.</p>
            </Reveal>
          </div>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 max-w-3xl">
            {team.map((m, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <div className="group overflow-hidden rounded-3xl bg-white h-full">
                  <div className="h-80 w-full overflow-hidden">
                    <img src={m.img} alt={`${m.name} — ${m.role}`} className="h-80 w-full object-cover object-top transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <div className="p-6">
                    <h3 className="font-serif text-2xl text-navy">{m.name}</h3>
                    <p className="text-sm text-gold font-semibold">{m.role}</p>
                    <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{m.body}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <Marquee />

      {/* MISSION CTA */}
      <section className="mx-auto max-w-4xl px-5 md:px-8 py-24 md:py-32 text-center">
        <Reveal><Eyebrow className="justify-center">Our Mission</Eyebrow></Reveal>
        <Reveal delay={0.05}>
          <h2 className="mt-6 font-serif text-4xl md:text-6xl leading-[1.05] text-navy">
            Better comfort. Better efficiency. A better home for years to come.
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="mt-6 text-muted-foreground text-lg leading-relaxed max-w-2xl mx-auto">
            We help Massachusetts homeowners build healthier, more efficient, more sustainable homes — lowering energy costs with high-efficiency HVAC systems and Mass Save® programs. Comfort that lasts, and value that grows over time.
          </p>
        </Reveal>
        <Reveal delay={0.15}>
          <Link to="/contact" className="mt-9 inline-flex items-center gap-2 rounded-full bg-navy px-8 py-4 text-sm font-semibold text-white transition-colors duration-300 hover:bg-navy/85">
            Request a Quote <ArrowRight className="h-4 w-4" />
          </Link>
        </Reveal>
      </section>
    </div>
  );
}
